import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Outcome = 'optimal' | 'acceptable' | 'poor';

interface FeedbackPanelProps {
  outcome: Outcome;
  feedback: string;
  learningPoints: string[] | undefined;
  onContinue: () => void;
}

const outcomeConfig: Record<
  Outcome,
  { label: string; color: string; bgColor: string; icon: typeof CheckCircle2 }
> = {
  optimal: {
    label: 'Optimal',
    color: 'text-feedback-optimal',
    bgColor: 'bg-green-50',
    icon: CheckCircle2,
  },
  acceptable: {
    label: 'Acceptable',
    color: 'text-feedback-acceptable',
    bgColor: 'bg-yellow-50',
    icon: AlertCircle,
  },
  poor: {
    label: 'Needs Improvement',
    color: 'text-feedback-poor',
    bgColor: 'bg-red-50',
    icon: XCircle,
  },
};

export function classifyOutcome(
  score: number,
  options: Array<{ score: number }>,
): Outcome {
  const maxScore = Math.max(...options.map((o) => o.score));
  if (score === maxScore) return 'optimal';
  if (score === 0) return 'poor';
  return 'acceptable';
}

export function FeedbackPanel({ outcome, feedback, learningPoints, onContinue }: FeedbackPanelProps) {
  const config = outcomeConfig[outcome];
  const Icon = config.icon;

  return (
    <Card className={cn('border-l-4', {
      'border-l-feedback-optimal': outcome === 'optimal',
      'border-l-feedback-acceptable': outcome === 'acceptable',
      'border-l-feedback-poor': outcome === 'poor',
    })}>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-5 w-5', config.color)} />
          <span className={cn('font-semibold', config.color)}>{config.label}</span>
        </div>
        <p className="text-sm leading-relaxed">{feedback}</p>
        {learningPoints && learningPoints.length > 0 && (
          <div className={cn('rounded-md p-3', config.bgColor)}>
            <p className="mb-2 text-sm font-medium">What to consider:</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {learningPoints.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </div>
        )}
        <Button onClick={onContinue}>Continue</Button>
      </CardContent>
    </Card>
  );
}
